import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { track } from '../analytics/events.js';
import './introTips.css';

// The intro tips, one view's short series (the owner's pick of the 2026-11-02 "Introrutor" canvas; replaced
// kartvy's first-visit guide, a modal full of text): one card at a time beside the control it is about, a looping
// scene on top that acts the thing out (introScenes.js), a title and one line, and a ring pulsing round that
// control. The map and the list stay usable while a card is open. Hoppa över or Escape ends the series, Nästa
// moves on, Klart ends it. Each view's series shows once per browser (localStorage `intro_tips_v1`); the old
// guide's `hide_onboarding` cookie (its "Visa inte igen", and test runs) skips them too. On phones the card is a
// sheet at the bottom. A card stands aside while the focus is in the control it points at (typing in the search),
// and the view hides it while a listing is open (`paused`). A series can start with the welcome (WELCOME_TIP, its
// `key` seen once for both views) and the shared tips (SHARED_TIPS) come one at a time when something happens
// (SharedIntroTips, requestIntroTip below).

const STORAGE_KEY = 'intro_tips_v1';
const CARD_WIDTH = 340;
const STAGE_HEIGHT = 200;
const GAP = 14;
const MARGIN = 12;
const RING_PAD = 5;

const readSeen = () => {
    try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (error) {
        return {};
    }
};

export const introSeen = (view) => {
    if (/(?:^|;\s*)hide_onboarding=true(?:;|$)/.test(document.cookie)) return true;
    return Boolean(readSeen()[view]);
};

export const markIntroSeen = (...keys) => {
    try {
        const seen = readSeen();
        keys.forEach((key) => { seen[key] = Date.now(); });
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
    } catch (error) {
        // Storage blocked: the tips show again next time.
    }
};

// The shared tips (SHARED_TIPS in introScenes.js) come when something happens — the filters opened, a search made —
// not as a series. One at a time: a request while any card is open is dropped (the next such moment asks again), and
// a view holds its own series back while one shows (useSharedIntroOpen). The view's SharedIntroTips shows them.
const shared = { host: null, pending: 0, shown: null, openCards: 0, listeners: new Set() };

const setSharedTip = (id) => {
    shared.shown = id;
    shared.listeners.forEach((listener) => listener(id));
};

export const requestIntroTip = (id, delay = 700) => {
    if (shared.pending || shared.shown || shared.openCards || introSeen(id)) return;
    shared.pending = window.setTimeout(() => {
        shared.pending = 0;
        if (!shared.host || shared.shown || shared.openCards || introSeen(id) || !shared.host.canShow(id)) return;
        setSharedTip(id);
    }, delay);
};

export const useSharedIntroOpen = () => {
    const [open, setOpen] = useState(() => Boolean(shared.shown));
    useEffect(() => {
        const listener = (id) => setOpen(Boolean(id));
        shared.listeners.add(listener);
        listener(shared.shown);
        return () => {
            shared.listeners.delete(listener);
        };
    }, []);
    return open;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// The card's place beside its target: the first of the tip's sides it fits on (the beak's offset along that edge
// points at the target's middle), else over the middle of `area` (or the window).
const placeCard = (rect, width, height, sides, area) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (rect) {
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        for (const side of sides) {
            if (side === 'below' || side === 'above') {
                const top = side === 'below' ? rect.bottom + GAP : rect.top - GAP - height;
                if (top < MARGIN || top + height > vh - MARGIN) continue;
                const left = clamp(rect.width > width ? rect.left : cx - width / 2, MARGIN, vw - width - MARGIN);
                return { left, top, side, beak: clamp(cx - left, 26, width - 26) };
            }
            const left = side === 'right' ? rect.right + GAP : rect.left - GAP - width;
            if (left < MARGIN || left + width > vw - MARGIN) continue;
            const top = clamp(cy - height / 2, MARGIN, vh - height - MARGIN);
            return { left, top, side, beak: clamp(cy - top, 26, height - 26) };
        }
    }
    const box = area?.getBoundingClientRect() || { left: 0, top: 0, width: vw, height: vh };
    return {
        left: clamp(box.left + box.width / 2 - width / 2, MARGIN, vw - width - MARGIN),
        top: clamp(box.top + box.height / 2 - height / 2, MARGIN, vh - height - MARGIN),
        side: null,
        beak: 0,
    };
};

const ringFor = (target) => {
    if (!target) return null;
    const rect = target.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const radius = parseFloat(window.getComputedStyle(target).borderTopLeftRadius) || 0;
    return {
        rect,
        left: rect.left - RING_PAD,
        top: rect.top - RING_PAD,
        width: rect.width + RING_PAD * 2,
        height: rect.height + RING_PAD * 2,
        radius: Math.min(radius, rect.height / 2, rect.width / 2) + RING_PAD,
    };
};

const sameLayout = (a, b) => {
    if (!a || !b) return a === b;
    const near = (x, y) => Math.abs((x || 0) - (y || 0)) < 0.5;
    const ringA = a.ring;
    const ringB = b.ring;
    const ringsSame = (!ringA && !ringB) || (ringA && ringB && near(ringA.left, ringB.left) && near(ringA.top, ringB.top)
        && near(ringA.width, ringB.width) && near(ringA.height, ringB.height));
    return a.side === b.side && near(a.left, b.left) && near(a.top, b.top) && near(a.beak, b.beak) && ringsSame;
};

const IntroTips = ({ view, series = view, tips: allTips, swedish, phone = false, phoneBottom = MARGIN, paused = false, onClose }) => {
    // A tip with a `key` of its own (the welcome) shows only until it has been seen, in either view.
    const tips = useMemo(() => allTips.filter((item) => !item.key || !introSeen(item.key)), [allTips]);
    const [index, setIndex] = useState(0);
    const [layout, setLayout] = useState(null);
    const [moving, setMoving] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
    const [targetFocused, setTargetFocused] = useState(false);
    const cardRef = useRef(null);
    const touch = useMemo(() => Boolean(window.matchMedia?.('(pointer: coarse)').matches), []);
    const still = useMemo(() => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches), []);
    const tip = tips[index];
    const lang = swedish ? 'sv' : 'en';
    const width = phone ? Math.min(viewportWidth - MARGIN * 2, 400) : CARD_WIDTH;
    const scale = width / CARD_WIDTH;
    const hidden = paused || targetFocused;

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Counted, so a shared tip never comes over this card.
    useEffect(() => {
        shared.openCards += 1;
        return () => {
            shared.openCards -= 1;
        };
    }, []);

    const update = useCallback(() => {
        const card = cardRef.current;
        if (!tip || !card) return;
        const target = tip.target?.() || null;
        // The control a tip can't do without has gone (its filter closed): it goes too, unseen, to come back.
        if (tip.requireTarget && !target) {
            onCloseRef.current?.();
            return;
        }
        const ring = ringFor(target);
        const place = phone
            ? { side: null }
            : placeCard(ring?.rect || null, width, card.offsetHeight, tip.placement || ['below'], tip.area?.() || null);
        const next = { ...place, ring: ring && { left: ring.left, top: ring.top, width: ring.width, height: ring.height, radius: ring.radius } };
        setLayout((current) => (sameLayout(current, next) ? current : next));
    }, [tip, phone, width]);

    useLayoutEffect(() => {
        if (!hidden) update();
    }, [update, hidden]);

    // The targets move with the page (a scroll, the window's size, the layout settling), so follow them now and
    // then rather than every frame, which would read the layout during the map's animations.
    useEffect(() => {
        if (hidden) return undefined;
        let frame = 0;
        const soon = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                update();
            });
        };
        const onResize = () => {
            setViewportWidth(window.innerWidth);
            soon();
        };
        const timer = window.setInterval(update, 400);
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', soon, true);
        return () => {
            window.clearInterval(timer);
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', soon, true);
        };
    }, [update, hidden]);

    // A card that changes place glides there; the first one appears in place.
    useEffect(() => {
        if (!layout || moving) return undefined;
        const frame = window.requestAnimationFrame(() => setMoving(true));
        return () => window.cancelAnimationFrame(frame);
    }, [layout, moving]);

    // The home a card is about may be further down the list.
    useEffect(() => {
        if (!tip?.reveal || hidden) return;
        const target = tip.target?.();
        if (!target) return;
        const rect = target.getBoundingClientRect();
        const bottom = phone ? window.innerHeight * 0.45 : window.innerHeight;
        if (rect.top < 0 || rect.bottom > bottom) {
            target.scrollIntoView({ block: phone ? 'start' : 'nearest', behavior: still ? 'auto' : 'smooth' });
        }
    }, [tip, hidden, phone, still]);

    // While the user is in the control a card points at (typing in the search), the card stands aside.
    useEffect(() => {
        const check = () => {
            const target = tip?.target?.();
            const active = document.activeElement;
            setTargetFocused(Boolean(target && active && active !== document.body && target.contains(active)));
        };
        check();
        document.addEventListener('focusin', check);
        document.addEventListener('focusout', check);
        return () => {
            document.removeEventListener('focusin', check);
            document.removeEventListener('focusout', check);
        };
    }, [tip]);

    const close = useCallback((reason) => {
        markIntroSeen(series, ...tips.map((item) => item.key).filter(Boolean));
        track(reason === 'done' ? 'onboarding.done' : 'onboarding.skip', {
            view, tip: tips[index]?.id, step: index + 1, steps: tips.length,
        });
        onClose?.();
    }, [series, view, tips, index, onClose]);

    // Doing what the card asks (the focus in its control) is as good as its button.
    useEffect(() => {
        if (targetFocused && tip?.doneOnFocus) close('done');
    }, [targetFocused, tip, close]);

    const next = () => {
        if (index < tips.length - 1) {
            setIndex(index + 1);
            return;
        }
        close('done');
        tip.onNext?.();
    };

    useEffect(() => {
        if (hidden) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close('skip');
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [close, hidden]);

    if (!tip || hidden) return null;

    const { Scene } = tip;
    const text = touch && tip.touchText ? tip.touchText : tip.text;
    const last = index === tips.length - 1;
    // The welcome isn't one of the series' dots.
    const counted = tips.filter((item) => !item.solo);
    const step = counted.indexOf(tip);
    const raised = tip.raised ? ' is-raised' : '';
    const cardStyle = phone
        ? { width, left: '50%', bottom: `calc(${phoneBottom}px + env(safe-area-inset-bottom, 0px))`, transform: 'translateX(-50%)' }
        : { width, transform: layout ? `translate3d(${Math.round(layout.left)}px, ${Math.round(layout.top)}px, 0)` : undefined };
    let beakStyle = null;
    if (layout?.side === 'below' || layout?.side === 'above') beakStyle = { left: layout.beak - 8 };
    else if (layout?.side) beakStyle = { top: layout.beak - 8 };
    const ring = layout?.ring;

    return createPortal(
        <>
            {ring && (
                <span
                    className={`intro-tip-ring${raised}`}
                    style={{ left: ring.left, top: ring.top, width: ring.width, height: ring.height, borderRadius: ring.radius }}
                    aria-hidden="true"
                />
            )}
            <div
                ref={cardRef}
                className={`intro-tip${phone ? ' is-phone' : ''}${layout ? ' is-placed' : ''}${moving ? ' is-moving' : ''}${raised}`}
                style={cardStyle}
                role="dialog"
                aria-modal="false"
                aria-labelledby="intro-tip-title"
            >
                {beakStyle && <span className={`intro-tip-beak is-${layout.side}`} style={beakStyle} aria-hidden="true" />}
                <div className="intro-tip-scene" style={{ height: STAGE_HEIGHT * scale }}>
                    <div
                        key={tip.id}
                        className={`intro-tip-stage${tip.bg ? ` is-bg-${tip.bg}` : ''}${touch ? ' is-touch' : ''}${still ? ' is-still' : ''}`}
                        style={{ transform: scale !== 1 ? `scale(${scale})` : undefined, '--it-still': `${tip.still}s` }}
                    >
                        <Scene sv={swedish} touch={touch} />
                    </div>
                </div>
                <div className="intro-tip-body">
                    <h2 id="intro-tip-title" className="intro-tip-title">{tip.title[lang]}</h2>
                    <p className="intro-tip-text">{text[lang]}</p>
                </div>
                <div className="intro-tip-foot">
                    {counted.length > 1 && step >= 0 && (
                        <>
                            <span className="intro-tip-dots" aria-hidden="true">
                                {counted.map((item, i) => <i key={item.id} className={i === step ? 'is-on' : ''} />)}
                            </span>
                            <span className="intro-tip-sr">
                                {swedish ? `Steg ${step + 1} av ${counted.length}` : `Step ${step + 1} of ${counted.length}`}
                            </span>
                        </>
                    )}
                    <span className="intro-tip-buttons">
                        {(!last || tip.skip) && (
                            <button type="button" className="intro-tip-skip" onClick={() => close('skip')}>
                                {tip.skip ? tip.skip[lang] : (swedish ? 'Hoppa över' : 'Skip')}
                            </button>
                        )}
                        <button type="button" className="intro-tip-next" onClick={next}>
                            {tip.next ? tip.next[lang] : (last ? (swedish ? 'Klart' : 'Done') : (swedish ? 'Nästa' : 'Next'))}
                        </button>
                    </span>
                </div>
            </div>
        </>,
        document.body,
    );
};

// The view's host for the shared tips: shows the one requested (requestIntroTip) when it can be shown there — its
// control on screen when it needs one (`requireTarget`), the view's own series seen first when it asks
// (`afterSeries`), and whatever else it asks (`ready`). Seen once per browser,
// like a series; one whose control goes away goes unseen.
export const SharedIntroTips = ({ view, tips, ...props }) => {
    const [id, setId] = useState(null);
    useEffect(() => {
        const host = {
            canShow: (key) => {
                const tip = tips[key];
                if (!tip) return false;
                if (tip.afterSeries && !introSeen(view)) return false;
                if (tip.requireTarget && !tip.target?.()) return false;
                return !tip.ready || tip.ready();
            },
        };
        const listener = (next) => setId(next);
        shared.host = host;
        shared.listeners.add(listener);
        return () => {
            shared.listeners.delete(listener);
            if (shared.host !== host) return;
            shared.host = null;
            window.clearTimeout(shared.pending);
            shared.pending = 0;
            if (shared.shown) setSharedTip(null);
        };
    }, [view, tips]);
    const tip = id ? tips[id] : null;
    const tipList = useMemo(() => (tip ? [tip] : []), [tip]);
    const close = useCallback(() => setSharedTip(null), []);
    if (!tip) return null;
    return <IntroTips key={id} view={view} series={id} tips={tipList} onClose={close} {...props} />;
};

export default IntroTips;
